import { useState } from "react";
import { useAuth } from "./AuthContext";

const C = {
  bg: "#f8f5f0", surface: "#ffffff", border: "#e8e4df",
  gold: "#d4af37", goldD: "#8a6f2e", goldBg: "rgba(212,175,55,0.08)",
  borderGold: "rgba(212,175,55,0.25)",
  text: "#1a1a2e", muted: "#6b7280", red: "#c0392b",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

export default function Login() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    // ★ Normalize before sending (2026-08-13): iPhone keyboards auto-append a
    //   trailing space and auto-capitalize the first letter of the email, and
    //   copy-pasted passwords often carry an invisible leading/trailing space.
    //   Any of those → "Invalid login credentials" even though everything the
    //   user SEES is correct. This burned Lisbell and Gabriella.
    try {
      // Without a timeout, sign-in against an unreachable server hangs for
      // minutes on "Signing in…" before the browser gives up — staff just see
      // an endless spinner and then a cryptic failure. Cap it at 15s.
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("network timeout")), 15000));
      const { error } = await Promise.race([
        signIn(email.trim().toLowerCase(), password.trim()),
        timeout,
      ]);
      if (error) {
        // Network/timeout failures mean the database provider is unreachable —
        // NOT bad credentials. Say so plainly, or staff keep retrying passwords
        // during a provider outage.
        const msg = String(error.message || "");
        setError(/fetch|network|timeout|load failed|failed to/i.test(msg)
          ? "Can't reach the server right now — this is a system outage, not a wrong password. Please try again in a few minutes."
          : msg);
      }
    } catch {
      setError("Can't reach the server right now — this is a system outage, not a wrong password. Please try again in a few minutes.");
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0f0f1e 0%, #1a1a2e 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: F, padding: 20 }}>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&family=Cormorant+Garamond:wght@400;600;700;800&display=swap" rel="stylesheet" />
      <div style={{ background: C.surface, borderRadius: 16, padding: 40, maxWidth: 420, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.4)", border: "1px solid " + C.borderGold }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ fontSize: 38, fontWeight: 800, color: C.gold, fontFamily: S, letterSpacing: "0.15em", marginBottom: 6 }}>ELYON</div>
          <div style={{ fontSize: 12, color: C.muted, letterSpacing: "0.2em", textTransform: "uppercase" }}>Med Spa Platform</div>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: C.gold, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 7 }}>Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoCapitalize="none" autoCorrect="off" spellCheck={false} inputMode="email" style={{ width: "100%", padding: "12px 14px", background: "#f3f4f6", border: "1px solid " + C.border, borderRadius: 8, fontSize: 14, color: C.text, outline: "none", boxSizing: "border-box", fontFamily: F }} />
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: C.gold, textTransform: "uppercase", letterSpacing: "0.1em" }}>Password</label>
              <button
                type="button"
                onClick={() => setShowPwd(s => !s)}
                style={{ background: "transparent", border: "none", color: C.muted, fontSize: 10, cursor: "pointer", fontFamily: F, textTransform: "uppercase", letterSpacing: "0.05em" }}
              >
                {showPwd ? "🙈 Hide" : "👁 Show"}
              </button>
            </div>
            <input type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" style={{ width: "100%", padding: "12px 14px", background: "#f3f4f6", border: "1px solid " + C.border, borderRadius: 8, fontSize: 14, color: C.text, outline: "none", boxSizing: "border-box", fontFamily: F }} />
          </div>
          {error && <div style={{ padding: "10px 14px", background: "rgba(192,57,43,0.1)", border: "1px solid rgba(192,57,43,0.25)", borderRadius: 8, color: C.red, fontSize: 12, marginBottom: 16 }}>{error}</div>}
          <button type="submit" disabled={loading} style={{ width: "100%", padding: "13px", background: "linear-gradient(135deg, " + C.gold + ", " + C.goldD + ")", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: F, letterSpacing: "0.05em", textTransform: "uppercase", opacity: loading ? 0.6 : 1 }}>{loading ? "Signing in..." : "Sign In"}</button>
        </form>
        <div style={{ textAlign: "center", marginTop: 20, fontSize: 11, color: C.muted }}>Secure login powered by Elyon</div>
      </div>
    </div>
  );
}
